import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import type { LinkedRecord, LinkedRecordStage, TicketDetail, UpdateTicketInput } from '@/api/ticketsApi'
import { ApiError } from '@/api/request'
import { NativeSelect } from '@/components/common/Field'
import { AssigneeMenu, StatusMenu } from '@/components/ticket/TicketQuickFields'
import { errorMessage } from '@/components/common/States'
import { useCustomers, useProducts } from '@/hooks/useDirectory'
import { ticketKey, useUpdateTicket } from '@/hooks/useTickets'
import { formatMinutes } from '@/lib/adminForms'
import { GXP_IMPACTS } from '@/lib/resolveForm'
import { SLA_TONE_CLASS, formatRemaining, slaCell, slaTone } from '@/lib/slaDisplay'
import { formatReceivedAt } from '@/lib/ticketDetails'
import { viewerTimeZone } from '@/lib/threadView'
import { cn } from '@/lib/utils'
import { useToast } from '@/providers/ToastProvider'

const railHeading = 'text-eyebrow font-medium uppercase text-ink-muted'
// The rail's panels: white cards on the page tint, as cgt-frontend's side panels.
const railCard = 'flex flex-col rounded-lg border border-line bg-background p-5'

/** Right rail (artboards 02 / 05): linked record + details. Resolve / reopen open the host's dialogs. */
export function TicketRail({
  ticket,
  routeId,
  now,
  canAssign,
  canUpdate,
  canResolve = false,
  canReopen = false,
  isAgent,
  onResolve,
  onReopen,
}: {
  ticket: TicketDetail
  routeId: string
  now: number
  canAssign: boolean
  canUpdate: boolean
  canResolve?: boolean
  canReopen?: boolean
  isAgent: boolean
  onResolve?: () => void
  onReopen?: () => void
}) {
  return (
    <aside className="flex w-[384px] shrink-0 flex-col gap-4 border-l border-line bg-surface-page px-4 py-6">
      <LinkedRecordPanel record={ticket.linkedRecord} />
      <DetailsPanel
        ticket={ticket}
        routeId={routeId}
        now={now}
        canAssign={canAssign}
        canUpdate={canUpdate}
        canResolve={canResolve && Boolean(onResolve)}
        canReopen={canReopen && Boolean(onReopen)}
        isAgent={isAgent}
        onResolve={onResolve}
        onReopen={onReopen}
      />
    </aside>
  )
}

function stageTone(status: string): 'done' | 'blocked' | 'pending' {
  const s = status.toLowerCase()
  if (s === 'done' || s === 'complete' || s === 'completed') return 'done'
  if (s === 'blocked' || s === 'failed') return 'blocked'
  return 'pending'
}

function Stage({ stage }: { stage: LinkedRecordStage }) {
  const tone = stageTone(stage.status)
  return (
    <li className="flex h-[30px] shrink-0 items-center gap-3">
      <span
        aria-label={stage.status}
        className={cn(
          'size-2.5 shrink-0 rounded-[5px]',
          tone === 'done' && 'bg-primary',
          tone === 'blocked' && 'border-2 border-priority-p1 bg-white',
          tone === 'pending' && 'border-2 border-marker-muted bg-white',
        )}
      />
      <span
        className={cn(
          'w-[86px] shrink-0 text-meta',
          tone === 'blocked' ? 'font-semibold text-priority-p1' : tone === 'pending' ? 'text-ink-faint' : 'text-ink-muted',
        )}
      >
        {stage.name}
      </span>
      <span className={cn('truncate text-meta', tone === 'blocked' ? 'text-priority-p1' : tone === 'pending' ? 'text-ink-faint' : 'text-ink-strong')}>
        {stage.summary}
      </span>
    </li>
  )
}

function LinkedRecordPanel({ record }: { record: LinkedRecord | null }) {
  const { t } = useTranslation()
  const products = useProducts(Boolean(record))
  if (!record) {
    return (
      <section className={cn(railCard, 'gap-3.5')}>
        <p className={railHeading}>{t('ticket.rail.linkedRecordPlain')}</p>
        <p className="text-meta text-ink-muted">{t('ticket.rail.noRecord')}</p>
      </section>
    )
  }
  const productName = products.data?.find((p) => p.key === record.product)?.name ?? record.product.toUpperCase()
  const snap = record.snapshot
  const key = (
    <span className="font-mono text-[15px] font-medium leading-[18px] text-primary">{record.key}</span>
  )
  return (
    <section className={cn(railCard, 'gap-3.5')} aria-label={t('ticket.rail.linkedRecord', { product: productName })}>
      <p className={railHeading}>{t('ticket.rail.linkedRecord', { product: productName })}</p>
      <div className="flex flex-col gap-0.5">
        {record.url ? (
          <a href={record.url} target="_blank" rel="noreferrer" title={t('ticket.rail.openInProduct')} className="w-fit hover:underline">
            {key}
          </a>
        ) : (
          key
        )}
        {snap?.subtitle ? <span className="text-meta text-ink-body">{snap.subtitle}</span> : null}
      </div>
      {snap?.stages && snap.stages.length > 0 ? (
        <ol className="flex flex-col pt-1">
          {snap.stages.map((s) => (
            <Stage key={s.name} stage={s} />
          ))}
        </ol>
      ) : null}
    </section>
  )
}

function Row({ label, children, action }: { label: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="group relative flex items-start">
      <span className="w-[104px] shrink-0 text-meta text-ink-muted">{label}</span>
      <div className="flex min-w-0 grow items-start gap-2">{children}</div>
      {/* Sits in the rail's right padding so values keep the design's full width. */}
      {action ? <div className="absolute -right-5 top-0">{action}</div> : null}
    </div>
  )
}

function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="shrink-0 rounded p-0.5 text-ink-placeholder opacity-0 hover:text-ink focus-visible:opacity-100 group-hover:opacity-100"
    >
      <Pencil className="size-3.5" />
    </button>
  )
}

/** A Date as the `yyyy-MM-ddTHH:mm` a datetime-local input takes, in the viewer's zone. */
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

function DetailsPanel({
  ticket,
  routeId,
  now,
  canAssign,
  canUpdate,
  canResolve,
  canReopen,
  isAgent,
  onResolve,
  onReopen,
}: {
  ticket: TicketDetail
  routeId: string
  now: number
  canAssign: boolean
  canUpdate: boolean
  canResolve: boolean
  canReopen: boolean
  isAgent: boolean
  onResolve?: () => void
  onReopen?: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const update = useUpdateTicket(routeId)
  const canEditTenant = canUpdate && isAgent
  const tenants = useCustomers(canEditTenant).data?.find((c) => c.id === ticket.customer.id)?.tenants ?? []
  const [editing, setEditing] = useState<'gxp' | 'tenant' | 'followUp' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  function save(input: UpdateTicketInput) {
    setNotice(null)
    update.mutate(
      { version: ticket.version, input },
      {
        onSuccess: () => setEditing(null),
        onError: (err) => {
          setEditing(null)
          if (err instanceof ApiError && (err.status === 409 || err.status === 412)) {
            // Stale version: someone else saved first. Refetch and tell the agent.
            void queryClient.invalidateQueries({ queryKey: ticketKey(routeId) })
            setNotice(t('ticket.rail.conflict'))
            notify({ title: t('ticket.rail.conflict'), tone: 'warning' })
          } else {
            setNotice(t('ticket.rail.saveError', { message: errorMessage(err) }))
          }
        },
      },
    )
  }

  const value = 'text-meta text-ink-strong'
  const done = ticket.status === 'resolved' || ticket.status === 'closed'
  const policy = ticket.slaPolicy
  const slaLabel = policy
    ? t('ticket.rail.slaWithPolicy', { priority: policy.priority, target: formatMinutes(policy.resolutionMins) })
    : t('ticket.rail.sla')

  let slaText: string
  let slaClass: string
  if (done && ticket.resolvedAt) {
    const met = !ticket.sla.breached && (!ticket.sla.dueAt || Date.parse(ticket.resolvedAt) <= Date.parse(ticket.sla.dueAt))
    const took = formatRemaining(ticket.resolvedAt, ticket.createdAt)
    slaText = met ? t('ticket.rail.met', { time: took }) : t('ticket.rail.overdue', { time: formatRemaining(ticket.resolvedAt, ticket.sla.dueAt).replace('−', '') })
    slaClass = met ? 'text-resolved-heading font-semibold' : 'text-sla-breach font-semibold'
  } else if (ticket.sla.clockRunning === false && (ticket.sla.dueAt || ticket.slaPolicy)) {
    // Clock first: a paused ticket shows the time left at pause, never "overdue".
    const cell = slaCell(ticket.sla, ticket.status, now)
    slaText = cell.text ? t('ticket.rail.pausedLeft', { time: cell.text }) : t('ticket.rail.pausedOnly')
    slaClass = cn(SLA_TONE_CLASS[cell.tone], 'font-semibold')
  } else if (!ticket.sla.dueAt) {
    slaText = '—'
    slaClass = 'text-ink-muted'
  } else {
    const remaining = formatRemaining(ticket.sla.dueAt, now)
    const overdue = remaining.startsWith('−')
    slaText = overdue ? t('ticket.rail.overdue', { time: remaining.slice(1) }) : t('ticket.rail.remaining', { time: remaining })
    slaClass = cn(SLA_TONE_CLASS[slaTone(ticket.sla, now)], 'font-semibold')
  }

  const receivedVia = ticket.context?.receivedVia
  const receivedAt = formatReceivedAt(ticket.context?.receivedAt, viewerTimeZone())
  const gxp = ticket.gxpImpact
  const deviation = ticket.deviationDecision
    ? ticket.deviationDecision === 'raised' && ticket.deviationRef
      ? t('ticket.deviation.raisedRef', { ref: ticket.deviationRef })
      : t(`ticket.deviation.${ticket.deviationDecision}`, { defaultValue: ticket.deviationDecision })
    : null

  return (
    <section className={cn(railCard, 'gap-2.5')} aria-label={t('ticket.rail.details')}>
      <p className={cn(railHeading, 'pb-1')}>{t('ticket.rail.details')}</p>

      <Row label={t('ticket.rail.status')}>
        <StatusMenu
          ticket={ticket}
          routeId={routeId}
          can={{ update: canUpdate && isAgent, resolve: canResolve && isAgent, reopen: canReopen && isAgent }}
          onResolve={() => onResolve?.()}
          onReopen={() => onReopen?.()}
        />
      </Row>

      <Row label={t('ticket.rail.assignee')}>
        <AssigneeMenu ticket={ticket} routeId={routeId} canAssign={canAssign} />
      </Row>
      {isAgent && !done ? (
        <Row
          label={t('ticket.rail.followUp')}
          action={canUpdate && editing !== 'followUp' ? <EditButton label={t('ticket.rail.editFollowUp')} onClick={() => setEditing('followUp')} /> : undefined}
        >
          {editing === 'followUp' ? (
            <div className="flex flex-col gap-1">
              <input
                type="datetime-local"
                autoFocus
                aria-label={t('ticket.rail.editFollowUp')}
                className="h-8 rounded-md border border-line px-2 text-meta"
                min={toLocalInput(new Date(now))}
                defaultValue={ticket.nextAction?.at ? toLocalInput(new Date(ticket.nextAction.at)) : ''}
                disabled={update.isPending}
                onBlur={(e) => {
                  const v = e.target.value
                  if (!v) return setEditing(null)
                  const iso = new Date(v).toISOString()
                  if (iso !== ticket.nextAction?.at) save({ followUpAt: iso })
                  else setEditing(null)
                }}
              />
              <span className="text-label text-ink-muted">{t('ticket.rail.followUpHelp')}</span>
              {ticket.nextAction?.at ? (
                <button type="button" className="w-fit text-label font-medium text-ink hover:underline" onMouseDown={() => save({ followUpAt: null })}>
                  {t('ticket.rail.clearFollowUp')}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col items-end gap-1">
              <span className={ticket.nextAction?.at ? value : 'text-meta text-ink-muted'}>
                {ticket.nextAction?.at ? new Date(ticket.nextAction.at).toLocaleString() : t('ticket.rail.noFollowUp')}
              </span>
              {ticket.nextAction?.noResponseAt ? (
                <span className="rounded-sm bg-status-escalated-bg px-2 py-[2px] text-label font-medium text-status-escalated-fg">
                  {t('ticket.rail.noResponse', { when: new Date(ticket.nextAction.reminderSentAt ?? ticket.nextAction.noResponseAt).toLocaleDateString() })}
                </span>
              ) : ticket.nextAction?.reminderSentAt ? (
                <span className="rounded-sm bg-note-bg px-2 py-[2px] text-label font-medium text-ink">
                  {t('ticket.rail.reminderSent', { when: new Date(ticket.nextAction.reminderSentAt).toLocaleDateString() })}
                </span>
              ) : null}
            </div>
          )}
        </Row>
      ) : null}
      <Row label={t('ticket.rail.customer')}>
        <span className={value}>{[ticket.customer.name, ticket.tenant?.name ?? ticket.environment?.name].filter(Boolean).join(' · ')}</span>
      </Row>
      {isAgent ? (
        <Row
          label={t('ticket.rail.tenant')}
          action={canEditTenant && editing !== 'tenant' ? <EditButton label={t('ticket.rail.editTenant')} onClick={() => setEditing('tenant')} /> : undefined}
        >
          {editing === 'tenant' ? (
            <NativeSelect
              autoFocus
              aria-label={t('ticket.rail.editTenant')}
              className="h-8 py-0 text-meta"
              defaultValue={ticket.tenant?.id ?? ''}
              disabled={update.isPending}
              onBlur={() => setEditing(null)}
              onChange={(e) => save({ tenantId: e.target.value || null })}
            >
              <option value="">{t('ticket.rail.noTenant')}</option>
              {ticket.tenant && !tenants.some((x) => x.id === ticket.tenant?.id) ? (
                <option value={ticket.tenant.id}>{ticket.tenant.name}</option>
              ) : null}
              {tenants.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </NativeSelect>
          ) : (
            <span className={ticket.tenant ? value : 'text-meta text-ink-muted'}>{ticket.tenant?.name ?? t('ticket.rail.noTenant')}</span>
          )}
        </Row>
      ) : null}
      {receivedVia ? (
        <Row label={t('ticket.rail.receivedVia')}>
          <span className={value}>
            {[t(`manualTicket.via.${receivedVia}`, { defaultValue: receivedVia }), receivedAt].filter(Boolean).join(' · ')}
          </span>
        </Row>
      ) : null}
      <Row label={t('ticket.rail.product')}>
        <span className={value}>{[ticket.product.name, ticket.component?.name].filter(Boolean).join(' · ')}</span>
      </Row>
      <Row label={t('ticket.rail.formVersion')}>
        {ticket.formRef ? (
          <span className="font-mono text-label text-ink-strong">{[ticket.formRef, ticket.formVersion].filter(Boolean).join(' · ')}</span>
        ) : (
          <span className="text-meta text-ink-muted">—</span>
        )}
      </Row>
      <Row
        label={t('ticket.rail.gxpImpact')}
        action={canUpdate && isAgent && editing !== 'gxp' ? <EditButton label={t('ticket.rail.editGxp')} onClick={() => setEditing('gxp')} /> : undefined}
      >
        {editing === 'gxp' ? (
          <NativeSelect
            autoFocus
            aria-label={t('ticket.rail.editGxp')}
            className="h-8 py-0 text-meta"
            defaultValue={gxp}
            disabled={update.isPending}
            onBlur={() => setEditing(null)}
            onChange={(e) => save({ gxpImpact: e.target.value })}
          >
            {GXP_IMPACTS.map((g) => (
              <option key={g} value={g}>
                {t(`ticket.gxp.${g}`)}
              </option>
            ))}
          </NativeSelect>
        ) : (
          <span className={cn('text-meta', gxp === 'yes' ? 'font-medium text-priority-p1' : gxp === 'unassessed' ? 'text-ink-muted' : 'text-ink-strong')}>
            {t(`ticket.gxp.${gxp}`, { defaultValue: gxp })}
          </span>
        )}
      </Row>
      {isAgent && ticket.gxpNote ? (
        <Row label={t('ticket.rail.gxpNote')}>
          <span className="whitespace-pre-line text-meta text-ink-strong">{ticket.gxpNote}</span>
        </Row>
      ) : null}
      <Row label={slaLabel}>
        <span className={cn('font-mono text-label', slaClass)}>{slaText}</span>
      </Row>
      {isAgent ? (
        <Row label={t('ticket.rail.deviation')}>
          <span className={deviation ? value : 'text-meta text-ink-muted'}>{deviation ?? t('ticket.rail.noneLinked')}</span>
        </Row>
      ) : null}

      {notice ? (
        <p role="status" className="mt-2 rounded-md bg-status-customer-bg px-3 py-2 text-label text-status-customer-fg">
          {notice}
        </p>
      ) : null}
    </section>
  )
}
